import React, { MouseEvent, useCallback, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, Input, useSlug } from '@bloomberg/media-ui-core';
import type MediaServicesSearchClient from '@foundation/media-services-search-client/lib/index';
import { default as FocusTrap } from 'focus-trap-react';

import { DropdownMenuDesktop } from '../DropdownMenuDesktop';
import { useFetchThrottledSearchResults } from '../hooks/useFetchThrottledSearchResults';
import { THEMES } from '../types';

import styles from './SearchDesktop.module.css';

export type SearchDesktopProps = GlobalProps & {
  onClose?: (e: MouseEvent) => void;
  theme?: THEMES;
  desktopBreakpointClassName?: string;
  searchClient: MediaServicesSearchClient;
  trackingFunction: (action: string, eventLabel: string) => void;
  countryCode?: string;
};

export const SearchDesktop = React.forwardRef<
  HTMLInputElement,
  SearchDesktopProps
>(function SearchDesktopComponent(props: SearchDesktopProps, ref) {
  const {
    desktopBreakpointClassName,
    name = 'search-desktop',
    theme = THEMES.DARK,
    searchClient,
    onClose = () => {},
    trackingFunction,
    countryCode = 'US',
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [searchResults, fetchThrottledSearchResults] =
    useFetchThrottledSearchResults();
  const componentName = useSlug(name);
  const searchDesktopContainerSelector = 'search-desktop-container';

  const _trackingFunction = (
    submenuText: string,
    dropdownListItemText: string
  ) => {
    trackingFunction('click', `search-${submenuText}-${dropdownListItemText}`);
  };

  const cx = classes(
    [
      styles.searchDesktopContainer,
      searchDesktopContainerSelector,
      {
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
      },
    ],
    {
      name,
    }
  );

  const onInputChange = useCallback(
    (inputValue: string) => {
      setSearchValue(inputValue);
      fetchThrottledSearchResults(inputValue, searchClient, countryCode);
    },
    [searchClient, countryCode]
  );

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.currentTarget.value && event.key === 'Enter') {
      trackingFunction('click', `search-InputQuerySearch-${searchValue}`);
      window.location.href = `/search?query=${event.currentTarget.value}`;
    }
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        fallbackFocus: `.${searchDesktopContainerSelector}`,
        clickOutsideDeactivates: onClose as () => boolean,
        allowOutsideClick: true,
      }}
    >
      <div className={cx} data-component={componentName} ref={ref}>
        <div
          className={classes([
            styles.searchInputContainer,
            desktopBreakpointClassName,
          ])}
        >
          <Input
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            inputProps={{ autoFocus: true }}
            className={styles.searchInput}
            id="bloomberg.com-search-desktop"
            placeholder="Search Bloomberg"
            value={searchValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onEnterKeyDown}
          />
        </div>
        {searchValue.length > 0 && (
          <div className={styles.dropdown}>
            <DropdownMenuDesktop
              trackingFunction={_trackingFunction}
              desktopBreakpointClassName={desktopBreakpointClassName}
              key="search-dropdown-menu-desktop"
              menuItemId="search-dropdown-menu-desktop"
              theme={theme}
              submenuItems={searchResults}
            />
          </div>
        )}
      </div>
    </FocusTrap>
  );
});
